import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// The plain text of a rendered cell, flattening the React children react-markdown hands us.
function cellText(children) {
  if (children == null || children === false) return "";
  if (typeof children === "string" || typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(cellText).join("");
  if (children.props?.children != null) return cellText(children.props.children);
  return "";
}

// A money amount: a currency figure, a decimal figure, or a percentage. A bare integer (invoice numbers,
// ids, counts like "288751") is deliberately NOT an amount, so it stays left. Also rejects anything with
// letters, so dates ("29-Sep-2026"), words ("none", "Open") and ticks stay left.
// Accepts both the ASCII hyphen-minus and the Unicode minus sign (U+2212), which is what the backend emits.
const AMOUNT_CELL = /^[-+(−]?\s*(?:[£$€]\s*[-−]?\s*\d[\d,]*(?:\.\d+)?|\d[\d,]*\.\d+|\d[\d,]*%)\s*\)?$/;
const isAmount = (text) => {
  const t = text.trim();
  return t.length > 0 && !/[a-z]/i.test(t) && AMOUNT_CELL.test(t);
};

export const markdownComponents = {
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  ul: ({ children }) => (
    <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li>{children}</li>,
  code: ({ children }) => (
    <code className="bg-zinc-200 px-1 py-0.5 rounded text-sm font-mono">
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="bg-zinc-200 p-2 rounded overflow-x-auto mb-2">
      {children}
    </pre>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => (
    <a
      href={href}
      className="text-primary-600 underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  // A wide table would break the narrow chat bubble; scroll it sideways instead.
  table: ({ children }) => (
    <div className="overflow-x-auto mb-2">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead>{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  // The breakdown's total is a label-less row — every cell blank but the amount — rendered slightly larger
  // so it reads as the total.
  tr: ({ children }) => {
    const cells = React.Children.toArray(children);
    const isTotal =
      cells.length > 1 &&
      cells.slice(0, -1).every((cell) => cellText(cell.props?.children).trim() === "") &&
      isAmount(cellText(cells[cells.length - 1].props?.children));
    return (
      <tr className={`border-b border-zinc-200 last:border-0 ${isTotal ? "text-[13px]" : ""}`}>
        {children}
      </tr>
    );
  },
  // Right-align a header when the markdown marks the column right (GFM `--:`) or it names a figure column,
  // so it sits over its numbers.
  th: ({ children, style }) => {
    const right = style?.textAlign === "right" || NUMERIC_HEADERS.has(cellText(children).trim().toLowerCase());
    return (
      <th className={`px-2 py-1 font-semibold whitespace-nowrap ${right ? "text-right" : "text-left"}`}>
        {children}
      </th>
    );
  },
  // Right-align amount cells; bare integers (invoice numbers, ids), dates, words and ticks stay left.
  td: ({ children, style }) => {
    const text = cellText(children);
    const right = style?.textAlign === "right" || isAmount(text);
    return (
      <td
        className={`px-2 py-1 whitespace-nowrap ${right ? "text-right tabular-nums" : ""}`}
      >
        {children}
      </td>
    );
  },
};

// Header labels whose column holds figures — right-aligned so the header sits over its numbers even when
// the markdown carries no explicit column alignment.
const NUMERIC_HEADERS = new Set([
  "value", "amount", "outstanding", "total", "balance", "paid", "price", "qty", "quantity",
]);

// Shared markdown renderer for chat surfaces: GFM (so tables, strikethrough and
// autolinks parse) plus the styled component set above.
export function Markdown({ children }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {children}
    </ReactMarkdown>
  );
}
